import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import type {
  BillingPreview,
  BillingRun,
  BillingRunDetail,
  BillingRunStatus,
  RunBillingInput,
} from '../types.ts'

export function useBillingRuns(status?: BillingRunStatus) {
  return useQuery<BillingRun[]>({
    queryKey: ['billing', 'runs', status ?? 'all'],
    queryFn: () =>
      apiClient.get('/billing/runs', { params: status ? { status } : {} }).then((r) => r.data),
  })
}

export function useBillingRun(id: string | undefined) {
  return useQuery<BillingRunDetail>({
    queryKey: ['billing', 'runs', 'detail', id],
    queryFn: () => apiClient.get(`/billing/runs/${id}`).then((r) => r.data),
    enabled: Boolean(id),
  })
}

export function useBillingPreview() {
  return useMutation<BillingPreview, Error, RunBillingInput>({
    mutationFn: (input) => apiClient.post('/billing/preview', input).then((r) => r.data),
  })
}

export interface BillingRunCreated {
  runId: string
  contractsBilled: number
  itemsBilled: number
  totalAmountCents: number
  currency: string
}

export function useCreateBillingRun() {
  const qc = useQueryClient()
  return useMutation<BillingRunCreated, Error, RunBillingInput>({
    mutationFn: (input) => apiClient.post('/billing/run', input).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['billing'] }),
  })
}

export function useUpdateBillingRunStatus() {
  const qc = useQueryClient()
  return useMutation<
    BillingRun,
    Error,
    { id: string; status?: BillingRunStatus; notes?: string }
  >({
    mutationFn: ({ id, ...payload }) =>
      apiClient.patch(`/billing/runs/${id}`, payload).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['billing'] }),
  })
}

export function useDeleteBillingRun() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.delete(`/billing/runs/${id}`).then(() => undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['billing'] }),
  })
}

export interface SubmitToSapResult {
  contractsSubmitted: number
  itemsSubmitted: number
  itemsErrored: number
  errors: Array<{ contractId: string; message: string }>
}

export function useSubmitRunToSap() {
  const qc = useQueryClient()
  return useMutation<SubmitToSapResult, Error, string>({
    mutationFn: (runId) => apiClient.post(`/billing/runs/${runId}/submit-to-sap`, {}).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['billing'] }),
  })
}

export interface RetrySapResult {
  ok: boolean
  message?: string
  sapDocEntry?: number
}

export function useRetrySapItem() {
  const qc = useQueryClient()
  return useMutation<RetrySapResult, Error, string>({
    mutationFn: (itemId) => apiClient.post(`/billing/items/${itemId}/retry-sap`, {}).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['billing'] }),
  })
}
